import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { api, type AdminUserListItem, type AdminStoreWithSteps, type AdminSubscriptionInfo } from '@/services/api';
import { useAdminCabinetUrl } from '@/context/AdminCabinetUrlContext';
import { Loader2, RefreshCw, Users, ChevronDown, ChevronRight, Store, Check, X, Crown } from 'lucide-react';
import { GrantProModal } from '@/components/GrantProModal';

const ONBOARDING_LABELS: Record<string, string> = {
  onboarding_completed: 'Регистрация',
  access_token_instruction_completed: 'Access токен',
  yandex_store_instruction_completed: 'Магазин Яндекс',
  yandex_contract_confirmed: 'Договор Яндекс',
  logo_store_name_instruction_completed: 'Лого и название',
};

const LAUNCH_STEP_LABELS: Record<string, string> = {
  product_import: 'Импорт товаров',
  yandex_info: 'Инфо для Яндекс',
  yandex_contract: 'Договор с Яндекс',
};

const LAUNCH_STEP_IDS = ['product_import', 'yandex_info', 'yandex_contract'];

const STATUS_LABELS: Record<string, string> = {
  pending: 'Ожидает',
  approved: 'Одобрено',
  rejected: 'Отклонено',
};

type StoreFromApi = {
  store_id: string;
  store_name?: string | null;
  pimmer_user_id?: string | null;
  user_email?: string | null;
  user_emails?: string[];
  latest_status: string;
  latest_request_at: string;
  launch_steps: string[];
  completed_at: Record<string, string>;
  is_pro?: boolean;
  subscription?: AdminSubscriptionInfo | null;
};

function formatShortDate(s?: string | null): string {
  if (!s) return '';
  try {
    return new Date(s).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch {
    return '';
  }
}

function ProStoreBadge({ subscription }: { subscription?: AdminSubscriptionInfo | null }) {
  if (!subscription?.isActive) return null;
  return (
    <span
      data-testid="admin-pro-badge"
      className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-gradient-to-r from-amber-400 to-orange-500 text-white font-bold"
      title={`Pro до ${formatShortDate(subscription.expiresAt)}`}
    >
      <Crown className="w-3 h-3" fill="currentColor" />
      Pro до {formatShortDate(subscription.expiresAt)}
    </span>
  );
}

function formatDate(s: string) {
  return new Date(s).toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function userName(u: AdminUserListItem): string {
  const parts = [
    u.telegram_first_name,
    u.telegram_last_name,
    u.telegram_username ? `@${u.telegram_username}` : null,
  ].filter(Boolean);
  if (parts.length > 0) return parts.join(' ');
  return u.pimmer_user_id ? `Pimmer ${u.pimmer_user_id}` : `User #${u.id}`;
}

function StoreIdLink({ storeId, label, shopUrl }: { storeId: string; label?: string; shopUrl: string }) {
  if (!shopUrl) return <>{label ?? storeId}</>;
  return (
    <a
      href={shopUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="text-primary hover:underline"
      data-testid={`store-id-link-${storeId}`}
    >
      {label ?? storeId}
    </a>
  );
}

export function UsersAndStores() {
  const { buildShopUrl } = useAdminCabinetUrl();
  const [users, setUsers] = useState<AdminUserListItem[]>([]);
  const [stores, setStores] = useState<StoreFromApi[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [expandedUserId, setExpandedUserId] = useState<number | null>(null);
  const [expandedStores, setExpandedStores] = useState<Record<number, AdminStoreWithSteps[]>>({});
  const [loadingDetail, setLoadingDetail] = useState<number | null>(null);
  const [grantProFor, setGrantProFor] = useState<{ storeId: string; storeName?: string | null } | null>(null);
  const [cancellingProFor, setCancellingProFor] = useState<string | null>(null);

  const handleCancelPro = async (storeId: string) => {
    if (!window.confirm('Снять Pro-подписку с этого магазина?')) return;
    setCancellingProFor(storeId);
    try {
      await api.cancelProSubscription(storeId);
      await loadData();
    } catch (err) {
      console.error('Cancel Pro error:', err);
      const msg = err && typeof err === 'object' && 'response' in err
        ? (err as { response?: { data?: { error?: string } } }).response?.data?.error
        : null;
      alert(msg || 'Не удалось снять Pro');
    } finally {
      setCancellingProFor(null);
    }
  };

  const limit = 25;

  const loadData = async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const [usersRes, storesRes] = await Promise.all([
        api.getUsers({ page, limit, search: search || undefined }),
        api.getStores().catch(() => ({ stores: [] })),
      ]);
      setUsers(usersRes.users);
      setTotal(usersRes.total);
      setStores(storesRes.stores || []);
    } catch (err) {
      console.error('Failed to load users:', err);
      setUsers([]);
      setTotal(0);
      const storesRes = await api.getStores().catch(() => ({ stores: [] }));
      setStores(storesRes.stores || []);
      const detail = err && typeof err === 'object' && 'response' in err
        ? (err as { response?: { data?: { detail?: string; error?: string } } }).response?.data?.detail
        : undefined;
      setLoadError(detail || (err instanceof Error ? err.message : 'Ошибка загрузки'));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [page, search]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSearch(searchInput.trim());
    setPage(1);
  };

  const toggleExpand = async (userId: number) => {
    if (expandedUserId === userId) {
      setExpandedUserId(null);
      return;
    }
    setExpandedUserId(userId);
    if (expandedStores[userId]) return;
    setLoadingDetail(userId);
    try {
      const { stores } = await api.getUserDetail(userId);
      setExpandedStores((prev) => ({ ...prev, [userId]: stores }));
    } catch (err) {
      console.error('Failed to load user stores:', err);
    } finally {
      setLoadingDetail(null);
    }
  };

  const totalPages = Math.ceil(total / limit) || 1;

  return (
    <div className="container mx-auto px-6 py-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5" />
            Пользователи и магазины
          </CardTitle>
          <div className="flex gap-2 items-center">
            <form onSubmit={handleSearchSubmit} className="flex gap-2">
              <Input
                placeholder="Поиск: имя, username, email, pimmer_user_id"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="w-64 h-9"
              />
              <Button type="submit" variant="outline" size="sm">
                Найти
              </Button>
            </form>
            <Button variant="outline" size="sm" onClick={loadData} disabled={isLoading}>
              <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loadError && (
            <div className="mb-4 p-4 rounded-lg bg-destructive/10 text-destructive text-sm">
              {loadError}
            </div>
          )}
          {isLoading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <>
              {stores.length > 0 && (
                <div className="space-y-4 mb-6">
                  <h3 className="text-sm font-medium text-muted-foreground">Магазины (из заявок)</h3>
                  <div className="space-y-2">
                    {stores.map((s) => (
                      <div
                        key={s.store_id}
                        className="flex items-start gap-4 p-4 rounded-lg border bg-background"
                      >
                        <Store className="w-4 h-4 mt-0.5 shrink-0 text-muted-foreground" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-medium">{s.store_name || s.store_id}</p>
                            <ProStoreBadge subscription={s.subscription} />
                          </div>
                          <p className="text-sm text-muted-foreground">
                            {s.user_emails?.length ? s.user_emails.join(', ') : s.user_email}
                            {(s.user_emails?.length || s.user_email) && s.pimmer_user_id && ' • '}
                            {s.pimmer_user_id && (
                              <>
                                Pimmer:{' '}
                                <StoreIdLink
                                  storeId={s.store_id}
                                  label={s.pimmer_user_id}
                                  shopUrl={buildShopUrl(s.store_id)}
                                />
                              </>
                            )}
                          </p>
                          <p className="text-xs text-muted-foreground mt-1">
                            ID:{' '}
                            <StoreIdLink storeId={s.store_id} shopUrl={buildShopUrl(s.store_id)} />
                          </p>
                          <div className="flex flex-wrap gap-2 mt-2 items-center">
                            <span className="text-xs px-2 py-0.5 rounded bg-muted">
                              {STATUS_LABELS[s.latest_status] || s.latest_status}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {formatDate(s.latest_request_at)}
                            </span>
                            {LAUNCH_STEP_IDS.map((stepId) => {
                              const completed = s.launch_steps.includes(stepId);
                              return (
                                <span
                                  key={stepId}
                                  className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded ${
                                    completed ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' : 'bg-muted text-muted-foreground'
                                  }`}
                                >
                                  {completed ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                                  {LAUNCH_STEP_LABELS[stepId]}
                                </span>
                              );
                            })}
                          </div>
                          <div className="flex gap-2 mt-3">
                            {s.is_pro ? (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleCancelPro(s.store_id)}
                                disabled={cancellingProFor === s.store_id}
                                className="text-destructive"
                                data-testid={`admin-cancel-pro-${s.store_id}`}
                              >
                                {cancellingProFor === s.store_id && <Loader2 className="w-3 h-3 animate-spin mr-1" />}
                                Снять Pro
                              </Button>
                            ) : (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setGrantProFor({ storeId: s.store_id, storeName: s.store_name })}
                                data-testid={`admin-grant-pro-${s.store_id}`}
                              >
                                <Crown className="w-3 h-3 mr-1" />
                                Выдать Pro
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {users.length === 0 && stores.length === 0 && !loadError && (
                <p className="text-center text-muted-foreground py-12">Нет пользователей и магазинов</p>
              )}
              {users.length > 0 && (
            <div className="space-y-2">
              <h3 className="text-sm font-medium text-muted-foreground mb-2">Пользователи</h3>
              {users.map((u) => (
                <div key={u.id} className="rounded-lg border bg-background">
                  <button
                    type="button"
                    className="w-full flex items-center gap-2 p-4 text-left hover:bg-muted/50 transition-colors"
                    onClick={() => toggleExpand(u.id)}
                  >
                    {expandedUserId === u.id ? (
                      <ChevronDown className="w-4 h-4 shrink-0" />
                    ) : (
                      <ChevronRight className="w-4 h-4 shrink-0" />
                    )}
                    <div className="flex-1 min-w-0 grid grid-cols-[1fr_1fr_1fr_auto_auto_auto] gap-4 items-center">
                      <div>
                        <p className="font-medium truncate">{userName(u)}</p>
                        {u.pimmer_user_id && (
                          <p className="text-sm text-muted-foreground">
                            Pimmer: {u.pimmer_user_id}
                          </p>
                        )}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        Язык: {u.language || '—'}
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {(Object.keys(ONBOARDING_LABELS) as Array<keyof typeof ONBOARDING_LABELS>).map(
                          (key) =>
                            u[key] && (
                              <span
                                key={key}
                                className="inline-flex items-center gap-0.5 text-xs bg-muted px-1.5 py-0.5 rounded"
                                title={ONBOARDING_LABELS[key]}
                              >
                                <Check className="w-3 h-3 text-green-600" />
                                {ONBOARDING_LABELS[key]}
                              </span>
                            )
                        )}
                      </div>
                      <div className="text-sm">
                        <span className="text-muted-foreground">Магазинов:</span> {u.stores_count}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {formatDate(u.created_at)}
                      </div>
                      {loadingDetail === u.id && (
                        <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                      )}
                    </div>
                  </button>
                  {expandedUserId === u.id && (
                    <div className="border-t px-4 py-3 bg-muted/30">
                      {loadingDetail === u.id ? (
                        <div className="flex justify-center py-4">
                          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                        </div>
                      ) : expandedStores[u.id]?.length === 0 ? (
                        <p className="text-sm text-muted-foreground">Нет магазинов</p>
                      ) : (
                        <div className="space-y-3">
                          {expandedStores[u.id]?.map((s) => (
                            <div
                              key={s.store_id}
                              className="flex items-start gap-4 p-3 rounded-md bg-background border"
                            >
                              <Store className="w-4 h-4 mt-0.5 shrink-0 text-muted-foreground" />
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <p className="font-medium">{s.store_name || s.store_id}</p>
                                  <ProStoreBadge subscription={s.subscription} />
                                </div>
                                {s.store_address && (
                                  <p className="text-sm text-muted-foreground truncate">
                                    {s.store_address}
                                  </p>
                                )}
                                <p className="text-xs text-muted-foreground mt-1">
                                  ID:{' '}
                                  <StoreIdLink storeId={s.store_id} shopUrl={buildShopUrl(s.store_id)} />
                                </p>
                                <div className="flex flex-wrap gap-2 mt-2">
                                  {LAUNCH_STEP_IDS.map((stepId) => {
                                    const completed = s.launch_steps.includes(stepId);
                                    return (
                                      <span
                                        key={stepId}
                                        className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded ${
                                          completed ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' : 'bg-muted text-muted-foreground'
                                        }`}
                                      >
                                        {completed ? (
                                          <Check className="w-3 h-3" />
                                        ) : (
                                          <X className="w-3 h-3" />
                                        )}
                                        {LAUNCH_STEP_LABELS[stepId]}
                                      </span>
                                    );
                                  })}
                                </div>
                                <div className="flex gap-2 mt-3">
                                  {s.is_pro ? (
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => handleCancelPro(s.store_id)}
                                      disabled={cancellingProFor === s.store_id}
                                      className="text-destructive"
                                    >
                                      {cancellingProFor === s.store_id && <Loader2 className="w-3 h-3 animate-spin mr-1" />}
                                      Снять Pro
                                    </Button>
                                  ) : (
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => setGrantProFor({ storeId: s.store_id, storeName: s.store_name })}
                                    >
                                      <Crown className="w-3 h-3 mr-1" />
                                      Выдать Pro
                                    </Button>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
              )}
            </>
          )}
          {total > 0 && users.length > 0 && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t">
              <p className="text-sm text-muted-foreground">
                Всего: {total} • Страница {page} из {totalPages}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                >
                  Назад
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                >
                  Вперёд
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {grantProFor && (
        <GrantProModal
          storeId={grantProFor.storeId}
          storeName={grantProFor.storeName}
          onClose={() => setGrantProFor(null)}
          onGranted={() => { void loadData(); }}
        />
      )}
    </div>
  );
}
